import { useState, useCallback, useRef, useEffect } from 'react';

import { cartFunctions, isValidRawCart } from '../utils/promo-cart-utils';

const { fetchCartHelper, updateCartHelper, addToCartHelper } = cartFunctions;

export const useCart = () => {
  const isMounted = useRef(false);
  const [currentCartModel, setCurrentCartModel] = useState();
  // If a user adds/updates items in cart before initial cart call comes back
  const initialFetchComplete = useRef(false);
  const [isCartLoading, setIsCartLoading] = useState(true);

  const [thdCartReady] = useState(() => new Promise((resolve) => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('global.thd-cart-loaded', () => {
      resolve();
    });
  }));

  const handleReturnedCartModel = (newCartModel) => {
    initialFetchComplete.current = true;
    if (isMounted.current) setCurrentCartModel(newCartModel);
  };

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('promo.add-to-cart.success', (cartSuccessEvent) => {
      if (cartSuccessEvent?.eventName === 'success' && isValidRawCart(cartSuccessEvent.output?.CartModel)) {
        handleReturnedCartModel(cartSuccessEvent.output.CartModel);
      }
    });
    return () => { window.LIFE_CYCLE_EVENT_BUS.off('promo'); };
  }, []);

  useEffect(() => {
    thdCartReady.then(() => {
      if (!initialFetchComplete.current) {
        setIsCartLoading(true);
        fetchCartHelper()
          .then((newCartModel) => {
            // Doesn't allow cart to be overwritten if we have already received data.
            if (!initialFetchComplete.current) {
              handleReturnedCartModel(newCartModel);
            }
          })
          .catch(() => {
            setIsCartLoading(false);
          });
      }
    });
  }, [thdCartReady]);

  useEffect(() => {
    if (isMounted.current) {
      setIsCartLoading(false);
    }
  }, [currentCartModel]);

  const fetchCart = useCallback(() => {
    setIsCartLoading(true);

    return thdCartReady
      .then(fetchCartHelper)
      .then((newCartModel) => {
        handleReturnedCartModel(newCartModel);
        return newCartModel;
      })
      .catch((error) => {
        setIsCartLoading(false);
        return Promise.reject(error);
      });
  }, [thdCartReady]);

  const addToCart = useCallback((CartRequest) => {
    setIsCartLoading(true);

    return thdCartReady
      .then(() => addToCartHelper(CartRequest))
      .then((newCartModel) => {
        handleReturnedCartModel(newCartModel);
        window.LIFE_CYCLE_EVENT_BUS.trigger('promo.add-to-cart.success', { CartModel: newCartModel });

        return newCartModel;
      })
      .catch(fetchCart);
  }, [thdCartReady, fetchCart]);

  const updateCart = useCallback((CartRequest) => {
    setIsCartLoading(true);

    return thdCartReady
      .then(() => updateCartHelper(CartRequest))
      .then((newCartModel) => {
        handleReturnedCartModel(currentCartModel);
        window.LIFE_CYCLE_EVENT_BUS.trigger('promo.add-to-cart.success', { CartModel: newCartModel });

        return newCartModel;
      })
      .catch(fetchCart);
  }, [thdCartReady, currentCartModel, fetchCart]);

  return {
    CartModel: currentCartModel,
    addToCart,
    updateCart,
    isCartLoading,
    setIsCartLoading
  };
};
